import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { errorMessage } from '../services/apiError';
import { designShareService, DesignShareInfo } from '../services/designShareService';
import {
  SHARED_DESIGN_DEVICES,
  SHARED_DESIGN_DEVICE_KEYS,
  SharedDesignDeviceKey,
} from '../utils/sharedDesignDevices';

import { LiveSiteShareRow } from './LiveSite/LiveSiteShareRow';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';

import './ShareModal.css';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

/* ── Link row ── */
function ShareLinkRow({ url, copied, onCopy }: {
  url: string; copied: boolean; onCopy: () => void;
}) {
  return (
    <div className="share-modal__link-row">
      <input
        className="share-modal__link-input"
        value={url}
        readOnly
        onClick={(e) => (e.target as HTMLInputElement).select()}
      />
      <button className="share-modal__copy-btn" onClick={onCopy} type="button">
        {copied
          ? <><i className="fa-solid fa-check" /> Copied</>
          : <><i className="fa-regular fa-copy" /> Copy</>}
      </button>
    </div>
  );
}

/* ── Visibility toggle row ── */
function VisibilityToggle({ isPublic, toggling, onToggle }: {
  isPublic: boolean; toggling: boolean; onToggle: () => void;
}) {
  return (
    <div className="share-modal__visibility">
      <div className="share-modal__vis-info">
        <span className="share-modal__vis-label">{isPublic ? 'Public' : 'Private'}</span>
        <span className="share-modal__vis-hint">
          {isPublic
            ? 'Anyone with the link can view this design'
            : 'Only project members can view this design'}
        </span>
      </div>
      <label className={`share-modal__toggle ${toggling ? 'share-modal__toggle--loading' : ''}`}>
        <input
          type="checkbox"
          checked={isPublic}
          onChange={onToggle}
          disabled={toggling}
          className="share-modal__toggle-input"
        />
        <span className="share-modal__toggle-slider" />
        {toggling && <i className="fa-solid fa-spinner fa-spin share-modal__toggle-spin" />}
      </label>
    </div>
  );
}

/* ── Initial device for the shared preview ── */
function ShareDeviceSelector({ selected, onChange }: {
  selected: SharedDesignDeviceKey;
  onChange: (device: SharedDesignDeviceKey) => void;
}) {
  return (
    <div className="share-modal__device-section">
      <div className="share-modal__device-heading">
        <span>Open link as</span>
        <small>Choose the initial preview size</small>
      </div>
      <div className="share-modal__device-options" role="radiogroup" aria-label="Shared design preview size">
        {SHARED_DESIGN_DEVICE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected === key}
            className={`share-modal__device-option ${selected === key ? 'active' : ''}`}
            onClick={() => onChange(key)}
          >
            <i className={SHARED_DESIGN_DEVICES[key].icon} />
            <span>{SHARED_DESIGN_DEVICES[key].label}</span>
            <small>{SHARED_DESIGN_DEVICES[key].width}px</small>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Hook: load/create share ── */
function useShareLoader(projectId: string, isOpen: boolean) {
  const [share, setShare] = useState<DesignShareInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrCreate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setShare(await designShareService.createOrGetShare(projectId));
    } catch (err) {
      console.error('[ShareModal] Failed to create/get share', err);
      setError(errorMessage(err, 'We couldn’t generate the share link. Check your access and try again.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) loadOrCreate();
  }, [isOpen, projectId, loadOrCreate]);

  return { share, setShare, loading, error };
}

/* ── Hook: share actions ── */
async function toggleShareVisibility(
  projectId: string,
  share: DesignShareInfo,
  setShare: (s: DesignShareInfo | null) => void,
  setError: (error: string | null) => void,
  setToggling: (toggling: boolean) => void,
) {
  setToggling(true);
  setError(null);
  try {
    setShare(await designShareService.toggleVisibility(projectId, !share.is_public));
  } catch (err) {
    console.error('[ShareModal] Toggle failed', err);
    setError(errorMessage(err, 'We couldn’t change link visibility. Check your access and try again.'));
  } finally {
    setToggling(false);
  }
}

function useShareActions(
  projectId: string,
  share: DesignShareInfo | null,
  setShare: (s: DesignShareInfo | null) => void,
) {
  const [copied, setCopied] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const handleToggle = async () => {
    if (!share) return;

    await toggleShareVisibility(projectId, share, setShare, setError, setToggling);
  };

  const handleDelete = async (onClose: () => void) => {
    setError(null);
    try {
      await designShareService.deleteShare(projectId);
      setShare(null);
      onClose();
    } catch (err) {
      console.error('[ShareModal] Delete failed', err);
      setError(errorMessage(err, 'We couldn’t delete the share link. Refresh and try again.'));
    }
  };

  return {
    copied,
    toggling,
    error,
    handleCopy,
    handleToggle,
    handleDelete,
    clearCopied: () => setCopied(false),
  };
}

/* ── Modal body ── */
function ShareModalBody({
  share, copied, toggling, shareUrl, selectedDevice, onDeviceChange, onCopy, onToggle, onDelete,
}: {
  share: DesignShareInfo; copied: boolean; toggling: boolean; shareUrl: string;
  selectedDevice: SharedDesignDeviceKey;
  onDeviceChange: (device: SharedDesignDeviceKey) => void;
  onCopy: () => void; onToggle: () => void; onDelete: () => void;
}) {
  return (
    <div className="share-modal__body">
      <ShareLinkRow url={shareUrl} copied={copied} onCopy={onCopy} />
      <ShareDeviceSelector selected={selectedDevice} onChange={onDeviceChange} />
      <VisibilityToggle isPublic={share.is_public} toggling={toggling} onToggle={onToggle} />
      <button className="share-modal__delete-btn" onClick={onDelete} type="button">
        <i className="fa-solid fa-trash" /> Delete Link
      </button>
    </div>
  );
}

/* ── Title, then the project's live link once it has one ── */
function ShareModalTop({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  return (
    <>
      <ModalHeader title="Share Design" onClose={onClose} />
      <LiveSiteShareRow projectId={projectId} />
    </>
  );
}

/* ── Main modal ── */
export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, projectId }) => {
  const [selectedDevice, setSelectedDevice] = useState<SharedDesignDeviceKey>('desktop');
  const { share, setShare, loading, error: loadError } = useShareLoader(projectId, isOpen);
  const actions = useShareActions(projectId, share, setShare);

  if (!isOpen) return null;

  const shareUrl = share
    ? `${window.location.origin}/design/${projectId}/${share.share_id}?device=${selectedDevice}`
    : '';

  const handleDeviceChange = (device: SharedDesignDeviceKey) => {
    setSelectedDevice(device);
    actions.clearCopied();
  };

  return createPortal(
    <ModalBackdrop isVisible={isOpen} onClick={onClose}>
      <ModalContent onClose={onClose} className="auth-modal-content share-modal">
        <ShareModalTop projectId={projectId} onClose={onClose} />
        {loading ? (
          <div className="share-modal__loading">
            <i className="fa-solid fa-spinner fa-spin" /> Generating link…
          </div>
        ) : share ? (
          <>
            <ShareModalBody
              share={share}
              copied={actions.copied}
              toggling={actions.toggling}
              shareUrl={shareUrl}
              selectedDevice={selectedDevice}
              onDeviceChange={handleDeviceChange}
              onCopy={() => actions.handleCopy(shareUrl)}
              onToggle={actions.handleToggle}
              onDelete={() => actions.handleDelete(onClose)}
            />
            {actions.error && <div className="share-modal__error">{actions.error}</div>}
          </>
        ) : (
          <div className="share-modal__error">
            {loadError || 'We couldn’t generate the share link. Close this window and try again.'}
          </div>
        )}
      </ModalContent>
    </ModalBackdrop>,
    document.body,
  );
};
